// ClipRadar website — nav, radar hero, marketing film block, feature showcase engine.
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon, GradIcon, type IconName } from '../components/icons';
import { CRWLogo, MetalBtn, MonoNote, SectionHead } from '../components/primitives';
import { useSignedIn } from '../components/hooks';
import { DISCORD_INVITE } from './footer';
import { STILLS } from '../components/stills';
import { SCENES, type SceneName } from '../components/scenes';

export interface ShowcaseItem {
  icon: IconName;
  title: string;
  text: string;
  scene: SceneName;
}

// ---- nav data (founder 2026-07-17: the pill stays but EXPANDED to content
// width, and the Features/Resources dropdowns come back) ----
const NAV_FEATURES: { label: string; desc: string; hash: string }[] = [
  { label: 'Visual search', desc: 'Describe the shot, get the shot', hash: '#visual' },
  { label: 'Speech search', desc: 'Find anything anyone said', hash: '#speech' },
  { label: 'Faces', desc: 'Search by who’s on screen', hash: '#faces' },
  { label: 'Ultra Search', desc: 'One sentence, every mode at once', hash: '#ultra' },
  { label: 'Premiere panel', desc: 'ClipRadar inside Premiere Pro', hash: '#premiere' },
];
const NAV_RESOURCES: { label: string; desc: string; to?: string; href?: string }[] = [
  { label: 'Docs', desc: 'How to use ClipRadar', to: '/docs' },
  { label: 'Compare', desc: 'ClipRadar vs the alternatives', to: '/compare' },
  { label: 'Blog', desc: 'Guides & footage-search tips', to: '/blog' },
  { label: 'Join our Discord', desc: 'Community & support', href: DISCORD_INVITE },
  { label: 'Contact', desc: 'Talk to the team', to: '/contact' },
];

// `routed` = rendered on a sub-page: section links navigate back to /#hash.
const NAV_KEYS = ['features', 'resources'] as const;
type NavKey = (typeof NAV_KEYS)[number];

export function SiteNav({ routed }: { routed?: boolean }) {
  const [open, setOpen] = useState<NavKey | null>(null);
  const [menu, setMenu] = useState(false);
  // at the very top of the page the pill is bare — the frost MATERIALIZES the
  // moment content scrolls beneath it (crw-nav-atop drops the glass layer)
  const [atop, setAtop] = useState(() => typeof window === 'undefined' || window.scrollY < 10);
  useEffect(() => {
    const onScroll = () => setAtop(window.scrollY < 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // "Sign in" flips to "My Licenses" (and the trial CTA to "Buy") on a live session.
  const signedIn = useSignedIn();
  const navRef = useRef<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- shared gliding viewport (the GetJumper pattern): one panel under the
  // pill; switching triggers GLIDES the box (left/size) while the old content
  // slides out with the direction of travel and the new one slides in ----
  const trigRefs = useRef<Record<NavKey, HTMLButtonElement | null>>({ features: null, resources: null });
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [vp, setVp] = useState<{ left: number; w: number; h: number } | null>(null);
  // ghost = the outgoing content layer during a switch; dir = where IT exits to
  const [ghost, setGhost] = useState<{ k: NavKey; dir: 'left' | 'right'; n: number } | null>(null);
  const ghostTimer = useRef<number | null>(null);
  const lastShownRef = useRef<NavKey>('features');
  // glide only between two open states — a fresh open must not slide across
  // from where the panel sat when it last closed
  const glide = useRef(false);

  const openMenu = (k: NavKey) => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    setOpen((cur) => {
      glide.current = cur != null;
      if (cur && cur !== k && !reduced) {
        const dir: 'left' | 'right' = NAV_KEYS.indexOf(k) > NAV_KEYS.indexOf(cur) ? 'left' : 'right';
        setGhost((g) => ({ k: cur, dir, n: (g?.n ?? 0) + 1 }));
        if (ghostTimer.current != null) window.clearTimeout(ghostTimer.current);
        ghostTimer.current = window.setTimeout(() => setGhost(null), 230);
      }
      return k;
    });
  };

  // measure the active trigger + content -> position/size the viewport box
  // (layout effect: the box must be placed before paint or a fresh open flashes
  // at its previous position)
  useLayoutEffect(() => {
    if (!open) return;
    const trig = trigRefs.current[open];
    const nav = navRef.current;
    const inner = measureRef.current;
    if (!trig || !nav || !inner) return;
    const nb = nav.getBoundingClientRect();
    const tb = trig.getBoundingClientRect();
    const w = inner.offsetWidth;
    const h = inner.offsetHeight;
    // align to the trigger's left edge, but never spill past the pill's right edge
    const left = Math.max(10, Math.min(tb.left - nb.left, nb.width - w - 10));
    setVp({ left, w, h });
  }, [open]);

  // close dropdowns/menu on outside tap, scroll, or Escape
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menu && !open) return;
    const shut = () => { setMenu(false); setOpen(null); };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (navRef.current && !navRef.current.contains(t) && !sheetRef.current?.contains(t)) shut();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') shut(); };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', shut, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); window.removeEventListener('scroll', shut); document.removeEventListener('keydown', onKey); };
  }, [menu, open]);

  // the mobile sheet owns the screen while open — lock the page scroll behind it
  useEffect(() => {
    if (!menu) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = prev; };
  }, [menu]);

  const shut = () => { setMenu(false); setOpen(null); };
  const go = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    shut();
    const el = document.querySelector(href);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 76, behavior: 'smooth' });
  };

  // hover intent: open instantly, close after a beat so the pointer can travel
  const hoverClose = () => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(null), 160);
  };

  // anchor link: smooth-scroll on the landing page, route back home from sub-pages
  const HashLink = ({ hash, style, className, children }: { hash: string; style?: CSSProperties; className?: string; children: ReactNode }) =>
    routed
      ? <Link to={{ pathname: '/', hash }} onClick={shut} className={className} style={{ textDecoration: 'none', ...style }}>{children}</Link>
      : <a href={hash} onClick={(e) => go(e, hash)} className={className} style={{ textDecoration: 'none', ...style }}>{children}</a>;

  const DropItem = ({ label, desc }: { label: string; desc: string }) => (
    <>
      <b style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{label}</b>
      <i style={{ display: 'block', fontStyle: 'normal', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 1 }}>{desc}</i>
    </>
  );

  // a render function, NOT an inline component — an inline component's identity
  // changes every render, which would remount the button (and drop hover/focus)
  const navTrigger = (k: NavKey, label: string) => {
    const isOpen = open === k;
    return (
      <button
        key={k} ref={(el) => { trigRefs.current[k] = el; }}
        className="crw-navtop" style={pillLink} aria-expanded={isOpen} aria-haspopup="true"
        onMouseEnter={() => openMenu(k)} onMouseLeave={hoverClose}
        onClick={() => { if (isOpen) { glide.current = false; setOpen(null); } else openMenu(k); }}
      >
        {label}
        {/* the 300ms flip the founder loved on getjumper — it visibly TURNS */}
        <CRWIcon d="chevdown" size={12} style={{ transition: 'transform 300ms var(--ease-out)', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
      </button>
    );
  };


  const featureLinks = (big?: boolean) => NAV_FEATURES.map((it) => (
    <HashLink key={it.label} hash={it.hash} className={big ? undefined : 'crw-nav-dditem'} style={big ? { fontSize: 15, fontWeight: 500, color: 'var(--fg-2)', padding: '10px 14px', borderRadius: 10 } : undefined}>
      {big ? it.label : <DropItem label={it.label} desc={it.desc} />}
    </HashLink>
  ));
  const resourceLinks = (big?: boolean) => NAV_RESOURCES.map((it) => {
    const bigStyle: CSSProperties = { textDecoration: 'none', fontSize: 15, fontWeight: 500, color: 'var(--fg-2)', padding: '10px 14px', borderRadius: 10 };
    const body = big ? it.label : <DropItem label={it.label} desc={it.desc} />;
    return it.href
      ? <a key={it.label} href={it.href} target="_blank" rel="noopener noreferrer" onClick={shut} className={big ? undefined : 'crw-nav-dditem'} style={big ? bigStyle : undefined}>{body}</a>
      : <Link key={it.label} to={it.to!} onClick={shut} className={big ? undefined : 'crw-nav-dditem'} style={big ? bigStyle : undefined}>{body}</Link>;
  });

  const signInLabel = signedIn ? 'My Licenses' : 'Sign in';
  const pillLink: CSSProperties = { fontSize: 13.5, padding: '7px 12px', borderRadius: 999 };

  // keep the last-open key so the panel fades out WITH its content on close
  if (open) lastShownRef.current = open;
  const shown = open ?? lastShownRef.current;
  const navContent = (k: NavKey) => (k === 'features' ? featureLinks() : resourceLinks());

  // the shared viewport: ALWAYS mounted (its content is what gets measured —
  // gating the mount on a measurement would deadlock) but invisible until both
  // open and measured; glides (left/size) only between two open states
  const visible = Boolean(open && vp);
  const navViewport = (
    <div
      className="crw-nav-viewport" role="menu" aria-hidden={!visible}
      onMouseEnter={() => { if (closeTimer.current != null) window.clearTimeout(closeTimer.current); }}
      onMouseLeave={hoverClose}
      style={{
        left: vp?.left ?? 0, width: vp?.w, height: vp?.h, transformOrigin: 'top center',
        opacity: visible ? 1 : 0,
        transform: visible || reduced ? 'none' : 'translateY(-6px) scale(0.96)',
        pointerEvents: visible ? 'auto' : 'none',
        visibility: visible ? 'visible' : 'hidden',
        transition: reduced
          ? 'opacity 140ms var(--ease-out), visibility 140ms'
          : `opacity ${visible ? 200 : 140}ms var(--ease-out), transform 220ms var(--ease-out), visibility 220ms${glide.current ? ', left 260ms var(--ease-out), width 260ms var(--ease-out), height 260ms var(--ease-out)' : ''}`,
      }}
    >
      <div key={shown} ref={measureRef} className={`crw-nav-vpcontent${ghost && open && !reduced ? (ghost.dir === 'left' ? ' crw-vpin-r' : ' crw-vpin-l') : ''}`}>
        {navContent(shown)}
      </div>
      {ghost && open ? (
        <div key={`ghost-${ghost.n}`} aria-hidden="true" className={`crw-nav-vpcontent ${ghost.dir === 'left' ? 'crw-vpout-l' : 'crw-vpout-r'}`} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          {navContent(ghost.k)}
        </div>
      ) : null}
    </div>
  );

  // The pill (N1) EXPANDED to content width (founder, 2026-07-17) with the
  // Features/Resources dropdowns back on board. Under 900px it relaxes to a
  // full-width bar so the burger sheet has room.
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, display: 'flex', justifyContent: 'center', padding: '16px 24px' }}>
      {/* an open dropdown or the mobile sheet forces the frost back on — a
          transparent pill can't visually anchor a floating panel */}
      <nav ref={navRef} className={`crw-glass crw-nav-frost crw-navpill${atop && !open && !menu ? ' crw-nav-atop' : ''}`} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2, padding: '7px 9px 7px 18px' }}>
        {routed
          ? <Link to="/" className="crw-logo-link" style={{ textDecoration: 'none' }}><CRWLogo size={22} /></Link>
          : <a href="#top" onClick={(e) => go(e, 'body')} className="crw-logo-link" style={{ textDecoration: 'none' }}><CRWLogo size={22} /></a>}
        <span style={{ flex: 1 }}></span>
        <span className="crw-navlinks" style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {navTrigger('features', 'Features')}
          {navTrigger('resources', 'Resources')}
          <HashLink hash="#pricing" className="crw-navtop" style={pillLink}>Pricing</HashLink>
          <Link to="/download" className="crw-navtop" style={pillLink}>Download</Link>
        </span>
        {navViewport}
        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)', margin: '0 8px 0 6px' }} className="crw-navlinks"></span>
        <span className="crw-navlinks" style={{ display: 'inline-flex' }}>
          <Link to="/account" className="crw-navtop" style={signedIn ? { ...pillLink, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)' } : pillLink}>{signInLabel}</Link>
        </span>
        <span className="crw-nav-join" style={{ marginLeft: 8, display: 'flex' }}>
          {/* Signed-in users already have a trial — sell the upgrade, not another trial.
              Silver cut: the nav is chrome — no warm glow riding every screenful. */}
          {signedIn
            ? <HashLink hash="#pricing" style={{ display: 'flex' }}><MetalBtn size="sm" neutral>Buy ClipRadar</MetalBtn></HashLink>
            : <Link to="/trial" style={{ textDecoration: 'none', display: 'flex' }}><MetalBtn size="sm" neutral>Start Free Trial</MetalBtn></Link>}
        </span>
        <button className="crw-burger" aria-label={menu ? 'Close menu' : 'Open menu'} aria-expanded={menu} onClick={() => { setOpen(null); setMenu((m) => !m); }}>
          <CRWIcon d={menu ? 'x' : 'menu'} size={17} />
        </button>
      </nav>
      {/* mobile sheet — OUTSIDE the pill: the pill's backdrop-filter would trap
          position:fixed descendants in its own containing block */}
      {menu ? (
        <>
          <div className="crw-mm-scrim" onClick={shut} aria-hidden="true"></div>
          <div className="crw-glass crw-nav-frost crw-mobile-menu" ref={sheetRef} role="menu">
            <div className="crw-mm-scroll">
              <span className="crw-nav-grouplabel">Features</span>
              <div className="crw-mm-grid">
                {NAV_FEATURES.map((it) => (
                  <HashLink key={it.label} hash={it.hash} className="crw-mm-link">{it.label}</HashLink>
                ))}
              </div>
              <span className="crw-nav-grouplabel">Resources</span>
              <div className="crw-mm-grid">
                {NAV_RESOURCES.map((it) => it.href
                  ? <a key={it.label} href={it.href} target="_blank" rel="noopener noreferrer" onClick={shut} className="crw-mm-link">{it.label}</a>
                  : <Link key={it.label} to={it.to!} onClick={shut} className="crw-mm-link">{it.label}</Link>)}
              </div>
              <div className="crw-mm-grid" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <HashLink hash="#pricing" className="crw-mm-link">Pricing</HashLink>
                <Link to="/download" onClick={shut} className="crw-mm-link">Download</Link>
                <Link to="/account" onClick={shut} className="crw-mm-link">{signInLabel}</Link>
              </div>
            </div>
            <div className="crw-mm-foot">
              {signedIn
                ? <HashLink hash="#pricing" style={{ display: 'flex' }}><MetalBtn style={{ flex: 1 }}>Buy ClipRadar</MetalBtn></HashLink>
                : <Link to="/trial" onClick={shut} style={{ textDecoration: 'none', display: 'flex' }}><MetalBtn style={{ flex: 1 }}>Start Free Trial</MetalBtn></Link>}
              <span className="crw-mm-note">40 free searches · no credit card required</span>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// The hero light field — diagonal cyan/teal beams + radar scope + blueprint grid,
// now alive: cursor parallax on the beams and scope, a grid spotlight that chases
// the pointer, drifting signal motes, and a one-shot sonar ping wherever you click.
export function RadarBackdrop() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pings, setPings] = useState<{ id: number; x: number; y: number; tc: string }[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const host = el.parentElement ?? el;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    let raf = 0;
    let tx = 0, ty = 0, cx = 0, cy = 0;        // parallax target / smoothed
    let mx = 50, my = 38, sx = 50, sy = 38;    // spotlight target / smoothed (%)
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      tx = Math.min(1, Math.max(-1, ((e.clientX - r.left) / r.width) * 2 - 1));
      ty = Math.min(1, Math.max(-1, ((e.clientY - r.top) / r.height) * 2 - 1));
      mx = ((e.clientX - r.left) / r.width) * 100;
      my = ((e.clientY - r.top) / r.height) * 100;
    };
    const tick = () => {
      cx += (tx - cx) * 0.05; cy += (ty - cy) * 0.05;
      sx += (mx - sx) * 0.12; sy += (my - sy) * 0.12;
      el.style.setProperty('--hx', cx.toFixed(4));
      el.style.setProperty('--hy', cy.toFixed(4));
      el.style.setProperty('--mx', `${sx.toFixed(2)}%`);
      el.style.setProperty('--my', `${sy.toFixed(2)}%`);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const onClick = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      const id = Date.now() + Math.random();
      const tc = `00:${String(Math.floor(10 + (x / 100) * 49)).padStart(2, '0')}:${String(Math.floor(10 + (y / 100) * 49)).padStart(2, '0')}`;
      setPings((p) => [...p.slice(-4), { id, x, y, tc }]);
      window.setTimeout(() => setPings((p) => p.filter((q) => q.id !== id)), 1700);
    };
    host.addEventListener('pointermove', onMove);
    host.addEventListener('click', onClick);
    return () => {
      cancelAnimationFrame(raf);
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('click', onClick);
    };
  }, []);

  const beams = [
    { left: '52%', top: '-22%', w: 130, h: 760, o: 0.40, blur: 50, d: '0s', c: 'rgba(74,227,255,0.85)', c2: 'rgba(60,230,160,0.4)' },
    { left: '63%', top: '-30%', w: 220, h: 900, o: 0.26, blur: 70, d: '1.2s', c: 'rgba(139,124,255,0.7)', c2: 'rgba(74,227,255,0.3)' },
    { left: '43%', top: '-14%', w: 70, h: 620, o: 0.48, blur: 36, d: '2.1s', c: 'rgba(74,227,255,0.9)', c2: 'rgba(139,124,255,0.35)' },
    { left: '74%', top: '-18%', w: 90, h: 560, o: 0.20, blur: 44, d: '0.6s', c: 'rgba(139,124,255,0.6)', c2: 'rgba(74,227,255,0.25)' },
    { left: '30%', top: '-26%', w: 150, h: 700, o: 0.15, blur: 64, d: '1.7s', c: 'rgba(74,227,255,0.6)', c2: 'rgba(139,124,255,0.3)' },
  ];
  const blips: [string, string, number][] = [
    ['62%', '28%', 0], ['34%', '38%', 1.1], ['71%', '58%', 2.3], ['44%', '67%', 0.6],
  ];
  const motes = Array.from({ length: 14 }, (_, i) => ({
    left: `${(i * 7.3 + 4) % 96}%`, size: 1.5 + (i % 3), dur: 9 + (i % 7) * 1.7, delay: i * 0.9,
  }));
  const rootStyle = { position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', '--hx': '0', '--hy': '0', '--mx': '50%', '--my': '38%' } as CSSProperties;

  return (
    <div ref={ref} aria-hidden="true" style={rootStyle}>
      {/* blueprint grid, faint */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'var(--bg-grid)', maskImage: 'radial-gradient(ellipse 65% 55% at 50% 40%, black 25%, transparent 72%)', WebkitMaskImage: 'radial-gradient(ellipse 65% 55% at 50% 40%, black 25%, transparent 72%)', opacity: 0.6 }}></div>
      {/* grid spotlight — the blueprint lights up around the cursor */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'var(--bg-grid)', opacity: 0.95, maskImage: 'radial-gradient(circle 250px at var(--mx) var(--my), black, transparent 72%)', WebkitMaskImage: 'radial-gradient(circle 250px at var(--mx) var(--my), black, transparent 72%)' }}></div>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle 320px at var(--mx) var(--my), rgba(74,227,255,0.075), transparent 70%)' }}></div>
      {/* diagonal light beams — drift toward the cursor */}
      <div style={{ position: 'absolute', inset: 0, transform: 'translate3d(calc(var(--hx) * 12px), calc(var(--hy) * 7px), 0)' }}>
        {beams.map((b, i) => (
          <span key={i} className="crw-beam" style={{ left: b.left, top: b.top, width: b.w, height: b.h, opacity: b.o, filter: `blur(${b.blur}px)`, animationDelay: b.d, background: `linear-gradient(180deg, transparent 0%, ${b.c} 30%, ${b.c2} 62%, transparent 100%)` }}></span>
        ))}
      </div>
      {/* radar scope — quiet, behind the beams, counter-parallax for depth */}
      <div className="crw-scope" style={{ position: 'absolute', left: '50%', top: '48%', width: 1100, height: 1100, transform: 'translate(-50%, -50%) translate3d(calc(var(--hx) * -16px), calc(var(--hy) * -10px), 0)', opacity: 0.8 }}>
        {[1, 0.74, 0.48].map((s) => (
          <span key={s} style={{ position: 'absolute', inset: `${(1 - s) * 50}%`, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)' }}></span>
        ))}
        <span className="crw-sweep"></span>
        {blips.map(([x, y, d], i) => (
          <span key={i} className="crw-blip" style={{ left: x, top: y, animationDelay: `${d}s` }}></span>
        ))}
      </div>
      {/* drifting signal motes */}
      {motes.map((m, i) => (
        <span key={i} className="crw-mote" style={{ left: m.left, bottom: '6%', width: m.size, height: m.size, animationDuration: `${m.dur}s`, animationDelay: `${m.delay}s` }}></span>
      ))}
      {/* sonar pings at click points */}
      {pings.map((p) => (
        <span key={p.id} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%` }}>
          <i className="crw-oneping"></i>
          <i className="crw-pingdot"></i>
          <i className="crw-pinglabel">CONTACT · {p.tc}</i>
        </span>
      ))}
      {/* vignette to page bg */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 70% at 50% 38%, transparent 30%, var(--bg-page) 95%)' }}></div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 240, background: 'linear-gradient(180deg, transparent, var(--bg-page))' }}></div>
    </div>
  );
}

export function Hero({ onJoin }: { onJoin: () => void }) {
  return (
    <header className="crw-hero" style={{ position: 'relative', padding: '170px 24px 60px', minHeight: 560 }}>
      <RadarBackdrop />
      <div style={{ position: 'relative', maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 26 }}>
        <span className="crw-eyebrow"><i></i>Local AI footage search for editors</span>
        <h1 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(38px, 4.9vw, 66px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.06, width: '100%' }}>
          <span className="crw-hero-line" style={{ display: 'block', whiteSpace: 'nowrap' }}>What if you could just type</span>
          <span className="crw-hero-line" style={{ display: 'block', whiteSpace: 'nowrap' }}>what you <span className="crw-shimmer">remember</span>?</span>
        </h1>
        <p style={{ margin: 0, fontSize: 17.5, lineHeight: 1.7, color: 'var(--fg-2)', maxWidth: 560, textWrap: 'pretty' }}>
          ClipRadar watches terabytes of footage so you don’t have to. Every frame, word and face — indexed on your machine, found the moment you ask.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <MetalBtn size="lg" onClick={onJoin} style={{ letterSpacing: '0.08em', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Request early access</MetalBtn>
            <button className="crw-glass crw-ghostbtn" onClick={() => { const el = document.querySelector('#visual'); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 40, behavior: 'smooth' }); }}>
              <CRWIcon d="play" size={13} style={{ marginRight: 2 }} /> See it in action
            </button>
          </div>
          <MonoNote>100% local · subscribe or own it · 110+ languages</MonoNote>
        </div>
      </div>
    </header>
  );
}

// Marketing film block — a designed "screening room" placeholder until the real
// 90s film exists (the play affordance is decorative; swap in a real player then).
export function FilmBlock() {
  // real frames of the project for the strip along the bottom
  const frames = [
    { img: STILLS.drone, pos: '50% 40%' },
    { img: STILLS.hosts, pos: '50% 32%' },
    { img: STILLS.townhall, pos: '50% 50%' },
    { img: STILLS.rooftop, pos: '52% 35%' },
    { img: STILLS.aerial3, pos: '50% 50%' },
    { img: STILLS.podium, pos: '48% 35%' },
    { img: STILLS.walking, pos: '50% 45%' },
  ];
  return (
    <section id="film" style={{ position: 'relative', padding: '20px 24px 90px' }}>
      <div style={{ maxWidth: 980, margin: '0 auto', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: '-60px -90px', background: 'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(31,210,244,0.09), transparent 70%)', pointerEvents: 'none' }}></div>
        <div style={{ position: 'relative', borderRadius: 18, padding: 1, background: 'linear-gradient(160deg, rgba(255,255,255,0.2), rgba(255,255,255,0.04) 30%, rgba(31,210,244,0.3) 70%, rgba(255,255,255,0.07))', boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 120px rgba(31,210,244,0.1)' }}>
          <div className="crw-filmposter" style={{ position: 'relative', borderRadius: 17, overflow: 'hidden', aspectRatio: '16 / 9', background: 'radial-gradient(ellipse 90% 100% at 50% -20%, rgba(31,210,244,0.13), transparent 55%), radial-gradient(ellipse 60% 80% at 85% 115%, rgba(139,124,255,0.12), transparent 60%), linear-gradient(180deg, #0B0B0E, #050506)' }}>
            {/* projector light cone */}
            <span aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '-30%', width: 560, height: '120%', transform: 'translateX(-50%)', background: 'conic-gradient(from 180deg at 50% 0%, transparent 42%, rgba(74,227,255,0.07) 50%, transparent 58%)', filter: 'blur(6px)', pointerEvents: 'none' }}></span>
            {/* blueprint grid, faint, fading down */}
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, backgroundImage: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, black, transparent 70%)', WebkitMaskImage: 'linear-gradient(180deg, black, transparent 70%)', opacity: 0.5 }}></span>
            {/* quiet radar arcs, bottom-left corner */}
            <span aria-hidden="true" style={{ position: 'absolute', left: -140, bottom: -140, width: 420, height: 420, pointerEvents: 'none' }}>
              {[1, 0.72, 0.45].map((s) => (
                <i key={s} style={{ position: 'absolute', inset: `${(1 - s) * 50}%`, borderRadius: '50%', border: '1px solid rgba(74,227,255,0.10)' }}></i>
              ))}
              <i className="crw-sweep" style={{ position: 'absolute', inset: 0, opacity: 0.4 }}></i>
            </span>
            {/* title composition */}
            <div className="crw-film-center" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: '0 24px 56px' }}>
              <MonoNote style={{ color: 'var(--signal-300)', letterSpacing: '0.22em' }}>ClipRadar presents</MonoNote>
              <div className="crw-h-grad" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4.2vw, 46px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.08 }}>
                Every frame. <span className="crw-shimmer">Findable.</span>
              </div>
              <span className="crw-playbtn crw-playsize" style={{ marginTop: 8, position: 'relative', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 0 40px rgba(31,210,244,0.3)' }}>
                <i className="crw-ping" style={{ inset: -14, fontStyle: 'normal' }}></i>
                <i className="crw-ping" style={{ inset: -14, fontStyle: 'normal', animationDelay: '2s' }}></i>
                <CRWIcon d="play" size={25} sw={1.5} style={{ marginLeft: 4, position: 'relative' }} />
              </span>
            </div>
            {/* filmstrip of abstract frames along the bottom */}
            <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '10px 14px 12px', display: 'flex', gap: 8, maskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)', background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.55))' }}>
              {frames.map((f, i) => (
                <span key={i} style={{ position: 'relative', flex: 1, height: 44, borderRadius: 5, overflow: 'hidden', border: i === 3 ? '1px solid rgba(74,227,255,0.65)' : '1px solid rgba(255,255,255,0.1)', boxShadow: i === 3 ? '0 0 16px rgba(74,227,255,0.25)' : 'none', background: '#0B0B0E' }}>
                  <img src={f.img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: f.pos, display: 'block', filter: i === 3 ? 'none' : 'brightness(0.75)' }} />
                  {i === 3 ? <i style={{ position: 'absolute', left: 4, bottom: 3, fontStyle: 'normal', fontFamily: 'var(--font-mono)', fontSize: 7.5, color: 'var(--signal-300)', background: 'rgba(0,0,0,0.7)', padding: '1px 4px', borderRadius: 3 }}>00:42:17 · 94%</i> : null}
                </span>
              ))}
            </div>
            {/* corner labels */}
            <div className="crw-film-right" style={{ position: 'absolute', left: 16, top: 13 }}>
              <MonoNote style={{ color: 'var(--fg-3)' }}>The film · 90 seconds</MonoNote>
            </div>
            <div className="crw-film-right" style={{ position: 'absolute', right: 16, top: 13 }}>
              <MonoNote style={{ color: 'var(--fg-4)' }}>Trailer — coming soon</MonoNote>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Feature showcase engine: clickable auto-advancing cards + live demo ----
export function FeatureShowcase({ id, label, title, sub, items, flip }: { id: string; label: string; title: string; sub: string; items: ShowcaseItem[]; flip?: boolean }) {
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0); // bump to replay scene
  const DURATION = 7000;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (boxRef.current) io.observe(boxRef.current);
    return () => io.disconnect();
  }, []);

  // replay the active scene when the section first scrolls into view
  const seen = useRef(false);
  useEffect(() => {
    if (inView && !seen.current) { seen.current = true; setCycle((c) => c + 1); }
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    timer.current = setTimeout(() => {
      setActive((a) => (a + 1) % items.length);
      setCycle((c) => c + 1);
    }, DURATION);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, cycle, inView]);

  const pick = (i: number) => { if (timer.current) clearTimeout(timer.current); setActive(i); setCycle((c) => c + 1); };
  const Scene: ComponentType = SCENES[items[active].scene];

  const cards = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => {
        const on = i === active;
        return (
          <button key={i} onClick={() => pick(i)} style={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', position: 'relative', overflow: 'hidden', padding: '16px 18px', borderRadius: 12, border: '1px solid', borderColor: on ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)', background: on ? 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))' : 'rgba(255,255,255,0.012)', boxShadow: on ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 14px 34px rgba(0,0,0,0.4)' : 'none', transition: 'background 200ms var(--ease-out), border-color 200ms var(--ease-out), box-shadow 200ms var(--ease-out)' }}>
            {on && inView ? <span key={cycle} className="crw-progress" style={{ animationDuration: `${DURATION}ms` }}></span> : null}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: on ? 7 : 0 }}>
              <span style={{ display: 'inline-flex', color: on ? 'var(--signal-400)' : 'var(--fg-4)', transition: 'color 200ms' }}>{on ? <GradIcon d={it.icon} size={16} /> : <CRWIcon d={it.icon} size={15} />}</span>
              <b style={{ fontSize: 15, fontWeight: 600, color: on ? 'var(--fg-1)' : 'var(--fg-3)', transition: 'color 200ms' }}>{it.title}</b>
            </div>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-3)', maxHeight: on ? 80 : 0, opacity: on ? 1 : 0, overflow: 'hidden', transition: 'max-height 300ms var(--ease-out), opacity 300ms var(--ease-out)' }}>{it.text}</p>
          </button>
        );
      })}
    </div>
  );

  const demo = (
    <div className="crw-showcase-demo" style={{ minWidth: 0, height: 495, position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: '-70px -90px', background: 'radial-gradient(ellipse 55% 55% at 50% 45%, rgba(31,210,244,0.10), transparent 70%)', pointerEvents: 'none' }}></div>
      <div key={`${active}-${cycle}`} style={{ height: '100%', position: 'relative' }}><Scene /></div>
    </div>
  );

  return (
    <section id={id} ref={boxRef} style={{ padding: '90px 24px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead label={label} title={title} sub={sub} />
        <div className="crw-showcase-grid" style={{ display: 'grid', gridTemplateColumns: flip ? '1.35fr 1fr' : '1fr 1.35fr', gap: 36, alignItems: 'center' }}>
          {flip ? demo : cards}
          {flip ? cards : demo}
        </div>
      </div>
    </section>
  );
}
